import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Collapse,
  IconButton,
  Divider,
  Chip,
  Paper,
  Stack
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { getAllCoverageErrors } from 'utils/projectCoverageRequirementsUtils';
import insurance_types from 'constants/insurance_types';

// Insurance type mapping
const INSURANCE_TYPE_LABELS = {
  'GENERAL LIABILITY': 'GL',
  'AUTOMOBILE LIABILITY': 'AU',
  'UMBRELLA/EXCESS LIABILITY': 'UMB',
  'WORKERS COMPENSATION': 'WC',
  'COMMERCIAL GENERAL LIABILITY': 'GL',
  'PROFESSIONAL LIABILITY': 'PL',
  'CUSTOM': 'CUSTOM'
};

const getRiskLevel = (exposure) => {
  if (!exposure || exposure === 0) return { level: 'LOW', color: 'rgba(0, 128, 0, 0.1)', textColor: 'green' };
  if (exposure > 500000) return { level: 'HIGH', color: 'rgba(139, 0, 0, 0.1)', textColor: 'darkred' };
  if (exposure > 100000) return { level: 'MEDIUM', color: 'rgba(225, 165, 0, 0.1)', textColor: 'chocolate' };
  return { level: 'LOW', color: 'rgba(0, 128, 0, 0.1)', textColor: 'green' };
};

const getComplianceColor = (percentage) => {
  if (percentage >= 90) return { color: 'rgba(0, 128, 0, 0.1)', barColor: 'green' };
  if (percentage >= 70) return { color: 'rgba(225, 165, 0, 0.1)', barColor: 'chocolate' };
  return { color: 'rgba(139, 0, 0, 0.1)', barColor: 'darkred' };
};

export default function ComplianceIndicator({ 
  coverages = [], 
  references = {},
  isEdit = false,
  certificateData = {}
}) {
  const [expanded, setExpanded] = useState(false);
  const [exposureBreakdown, setExposureBreakdown] = useState([]);
  const [totalExposure, setTotalExposure] = useState(0);
  const [compliancePercentage, setCompliancePercentage] = useState(0);

  // Calculate exposure gaps and compliance in real-time
  useEffect(() => {
    calculateExposureAndCompliance();
  }, [coverages, references]);

  const calculateExposureAndCompliance = () => {
    const breakdown = [];
    let totalGap = 0;
    let totalRequirements = 0;
    let requirementsMet = 0;

    // Process each coverage type
    coverages.forEach(coverage => {
      if (!coverage || !coverage.insuranceType) return;
      
      const insuranceType = coverage.insuranceType;
      const typeLabel = INSURANCE_TYPE_LABELS[insuranceType.toUpperCase()] || insuranceType;
      const coverageTypeConfig = insurance_types[insuranceType];
      const referenceValues = references[coverage.name || coverage.insuranceType] || {};
      
      // Use the existing error calculation logic
      const errors = getAllCoverageErrors(
        coverage,
        referenceValues,
        coverageTypeConfig
      );
      
      // Parse limits for exposure calculation
      let coverageLimits = coverage.limits || {};
      if (typeof coverageLimits === 'string') {
        try {
          coverageLimits = JSON.parse(coverageLimits);
        } catch (e) {
          coverageLimits = {};
        }
      }

      let referenceLimits = referenceValues.limits || {};
      if (typeof referenceLimits === 'string') {
        try {
          referenceLimits = JSON.parse(referenceLimits);
        } catch (e) {
          referenceLimits = {};
        }
      }

      // Calculate total exposure gap and compliance for this coverage type
      let totalCoverageGap = 0;
      let coverageRequirementsMet = 0;
      let coverageTotalRequirements = 0;

      // Check limits based on coverage type configuration
      if (coverageTypeConfig?.limits) {
        coverageTypeConfig.limits.forEach(limitKey => {
          const requiredValue = parseFloat(referenceLimits[limitKey] || 0);
          const actualValue = parseFloat(coverageLimits[limitKey] || 0);
          
          // Only count as a requirement if there's a required value
          if (requiredValue > 0) {
            coverageTotalRequirements++;
            totalRequirements++;
            
            // Check if requirement is met
            if (actualValue >= requiredValue) {
              coverageRequirementsMet++;
              requirementsMet++;
            } else {
              // Add the gap to total
              const gap = requiredValue - actualValue;
              totalCoverageGap += gap;
            }
          }
        });
      }

      // Check other requirements (additionalInsured, subrogationWaiver, etc.)
      const checkBooleanRequirement = (key) => {
        const required = referenceValues[key];
        const actual = coverage[key];
        if (required === true || required === 'Yes') {
          coverageTotalRequirements++;
          totalRequirements++;
          if (actual === true || actual === 'Yes') {
            coverageRequirementsMet++;
            requirementsMet++;
          }
        }
      };

      // Check coverage-specific requirements
      if (coverageTypeConfig) {
        // Check additional insured and subrogation waiver
        checkBooleanRequirement('additionalInsured');
        checkBooleanRequirement('subrogationWaiver');
        
        // Check aggregate applies per (for GL)
        if (insuranceType === 'GL' && referenceValues.aggregateAppliesPer) {
          const requiredAggregates = Array.isArray(referenceValues.aggregateAppliesPer) 
            ? referenceValues.aggregateAppliesPer 
            : [referenceValues.aggregateAppliesPer];
          const actualAggregates = coverage.aggregateAppliesPer || [];
          
          if (requiredAggregates.length > 0) {
            coverageTotalRequirements++;
            totalRequirements++;
            
            const hasAllRequired = requiredAggregates.every(req => 
              actualAggregates.includes(req)
            );
            
            if (hasAllRequired) {
              coverageRequirementsMet++;
              requirementsMet++;
            }
          }
        }
        
        // Check exclusion for WC
        if (insuranceType === 'WC' && referenceValues.exclusion !== undefined) {
          coverageTotalRequirements++;
          totalRequirements++;
          if (coverage.exclusion === referenceValues.exclusion) {
            coverageRequirementsMet++;
            requirementsMet++;
          }
        }
      }

      // Add to breakdown if there are errors or gaps
      if (errors > 0 || totalCoverageGap > 0) {
        breakdown.push({
          type: typeLabel,
          name: coverage.name || insuranceType,
          totalGap: totalCoverageGap,
          errors: errors,
          requirementsMet: coverageRequirementsMet,
          totalRequirements: coverageTotalRequirements
        });
        totalGap += totalCoverageGap;
      }
    });

    // Calculate overall compliance percentage based on all requirements
    const compliance = totalRequirements > 0 
      ? Math.round((requirementsMet / totalRequirements) * 100)
      : 100;

    setExposureBreakdown(breakdown);
    setTotalExposure(totalGap);
    setCompliancePercentage(compliance);
  };

  const risk = getRiskLevel(totalExposure);
  const complianceColor = getComplianceColor(compliancePercentage);

  const formatAmount = (amount) => {
    if (!amount || amount === 0) return '$0';
    return `$${amount.toLocaleString()}`;
  };

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
      <Box>
        {/* Header Section */}
        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Certificate Compliance
            {compliancePercentage >= 90 ? (
              <CheckCircleIcon sx={{ color: 'green', fontSize: 20 }} />
            ) : compliancePercentage >= 70 ? (
              <WarningIcon sx={{ color: 'chocolate', fontSize: 20 }} />
            ) : (
              <ErrorIcon sx={{ color: 'darkred', fontSize: 20 }} />
            )}
          </Typography>
          <Chip
            label={`${risk.level} RISK`}
            size="small"
            sx={{ 
              backgroundColor: risk.color,
              color: risk.textColor,
              fontWeight: 500,
              borderRadius: '5px'
            }}
          />
        </Box>

        {/* Compliance Bar */}
        <Box sx={{ mb: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Compliance: {compliancePercentage}%
            </Typography>
            <Box display="flex" alignItems="center">
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Total Exposure: {formatAmount(totalExposure)}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
                sx={{ p: 0.5 }}
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>
          <LinearProgress
            variant="determinate"
            value={compliancePercentage}
            sx={{ 
              height: 8, 
              borderRadius: 4,
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: complianceColor.barColor,
                borderRadius: 4
              }
            }}
          />
        </Box>

        {/* Expandable Breakdown Section */}
        <Collapse in={expanded}>
          <Divider sx={{ mb: 2 }} />
          <Box>
            <Typography variant="subtitle2" fontWeight="500" sx={{ mb: 1.5 }}>
              Exposure Gap Breakdown by Coverage Type:
            </Typography>
            
            {exposureBreakdown.length > 0 ? (
              <>
                <Stack spacing={1}>
                  {exposureBreakdown.map((item, idx) => (
                    <Box key={idx} display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip 
                          label={item.type} 
                          size="small" 
                          sx={{ 
                            minWidth: 45,
                            fontWeight: 600,
                            bgcolor: item.errors > 0 ? 'rgba(139, 0, 0, 0.1)' : 'rgba(128, 0, 128, 0.1)',
                            color: item.errors > 0 ? 'darkred' : 'purple'
                          }} 
                        />
                        <Typography variant="body2">
                          {item.name}
                        </Typography>
                        {item.totalRequirements > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({item.requirementsMet}/{item.totalRequirements} met)
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="body2" fontWeight="500" color="error.main">
                        {formatAmount(item.totalGap)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
                
                <Divider sx={{ my: 2 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body1" fontWeight="600">
                    Total Exposure Gap:
                  </Typography>
                  <Typography variant="body1" fontWeight="600" color={risk.textColor}>
                    {formatAmount(totalExposure)}
                  </Typography>
                </Box>
              </>
            ) : (
              <Box display="flex" alignItems="center" gap={1} sx={{ py: 2 }}>
                <CheckCircleIcon sx={{ color: 'green' }} />
                <Typography variant="body2" color="text.secondary">
                  All coverage requirements are met. No exposure gaps detected.
                </Typography>
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    </Paper>
  );
}